import Promise from 'bluebird';
import bootbox from 'bootbox';
import _ from 'lodash';
import moment from 'moment';

import { CONFIG } from '../../../config';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import api from '../../api';
import infoBoard from '../../core/infoboard';
import worldMap from '../../core/map';
import templates from '../../core/templates';
import translate from '../../core/tools/translate';
import environment from '../../environment';
import { customScrollbar, isLayerEnabled } from '../../shared';
import { selectMarker } from '../../shared/map';
import afterSales from '../../api/endpoints/afterSales';
import DataTable from '../../core/charts/Datatable';

import numeral from 'numeral';
import { DealerTypes } from '../../../constants/DealerTypes';
import { exportStore, storeExportType } from '../../core/export/afterSales/stores';

const LayerControls = (name) => require('../../controls').get(name);
const LayerInfo = (name) => require('./../index').get(name);
const currentLayer = (name) => LayerControls(name).getLayer();
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const _getContainer = options => infoBoard.createContainer(options);

const _getImage = (feature) =>
    new Promise((resolve) => {
        const id = feature.properties.IdOriginal;

        const images = {
            hasPhoto: true,
            original: `${CONFIG.STORE_IMAGES_HOST}/${id}.jpg`,
            thumbnail: `${CONFIG.STORE_THUMBS_HOST}/${id}.jpg`
        };

        const thumbnail = new Image();

        thumbnail.onload = () => resolve(images);

        thumbnail.onerror = () => {
            const latlng = {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0]
            };
            resolve({
                hasPhoto: false,
                original: '#',
                thumbnail: `https://maps.googleapis.com/maps/api/streetview?size=${worldMap.getThumbImageSize()}&location=${latlng.lat},${latlng.lng}&sensor=false&key=${CONFIG.GOOGLE_MAPS_API_KEY}`
            });
        };

        thumbnail.src = images.thumbnail;
    });

const _calculateSalesVariation = (data, currentYear) => {
    const previousYear = currentYear - 1;
    const currentData = data.filter(entry => entry.year === currentYear);
    const previousData = data.filter(entry => entry.year === previousYear);
    const previousDataByCurrent = (previousData.slice(0, (currentData.length)) || []);

    const totalCurrentYear = currentData.reduce((acc, entry) => acc + entry.value, 0);
    const totalPreviousYear = previousDataByCurrent.reduce((acc, entry) => acc + entry.value, 0);

    const variationPercentage = (((totalCurrentYear - totalPreviousYear) / totalPreviousYear) * 100) / 100;

    const getPeriod = (data) => {
        const months = data.map(entry => entry.month);
        const firstMonth = Math.min(...months);
        const lastMonth = Math.max(...months);
        return `${moment().month(firstMonth - 1).format('MMM')} - ${moment().month(lastMonth - 1).format('MMM')}`;
    };

    const currentPeriod = getPeriod(currentData);
    const previousPeriod = getPeriod(previousDataByCurrent);

    const sales = {
        past: {
            year: previousYear,
            total: totalPreviousYear,
            period: previousDataByCurrent.length === 0 ? currentPeriod : previousPeriod
        },
        current: {
            year: currentYear,
            total: totalCurrentYear,
            period: currentPeriod
        },
        variation: variationPercentage,
        firstPeriod: previousDataByCurrent.length === 0 ? currentPeriod : previousPeriod,
        lastPeriod: currentPeriod
    };

    return sales;
};

const _getSales = (name, id) => {
    return afterSales.get.getSalesByStoreId(id).then((resp) => {
        if (!resp.length) {
            return {
                past: null,
                current: null,
                variation: null,
                firstPeriod: null,
                lastPeriod: null,
                chart: null,
                grid: null,
                gridVehicles: null,
            };
        }

        const groupedByYear = resp.reduce((acc, { year, month, value }) => {
            if (!acc[year]) {
                acc[year] = { total: 0, months: [] };
            }
            acc[year].total += value;
            acc[year].months.push(month);
            return acc;
        }, {});

        const vehiclesGroupedByYear = resp.reduce((acc, { year, month, vehicleCount }) => {
            if (!acc[year]) {
                acc[year] = { total: 0, months: [] };
            }
            acc[year].total += vehicleCount;
            acc[year].months.push(month);
            return acc;
        }, {});

        const resultTable = (array, formatValues = false) => {
            const output = Object.keys(array).map(year => {
                const { total, months } = array[year];
                const startMonth = months[0];
                const endMonth = months[months.length - 1];
                const Ano = parseInt(year);
                const Media = formatValues ? numeral(total / months.length).format('0,0.00') : numeral(total / months.length).format('0,0');
                const Total = formatValues ? numeral(total).format('0,0.00') : total;
                const Periodo = `${moment().month(startMonth - 1).format('MMM')} - ${moment().month(endMonth - 1).format('MMM')}`;

                return {
                    Name: name,
                    Ano,
                    Media,
                    Total,
                    Periodo
                };
            });

            return _.orderBy(output, ['Ano'], ['desc']);
        };

        const dataSource = resultTable(groupedByYear, true);
        const dataSourceVehicles = resultTable(vehiclesGroupedByYear);

        const columns = [
            { title: translate('Common.Store'), field: 'Name', hidden: true },
            { title: translate('Common.Year'), field: 'Ano' },
            { title: translate('Common.Period'), field: 'Periodo' },
            { title: `${translate('Common.Average')} (R$)`, field: 'Media', format: '{0:n2}' },
            { title: 'Total (R$)', field: 'Total', format: '{0:n0}' }
        ];

        const columnsVehicle = [
            { title: translate('Common.Store'), field: 'Name', hidden: true },
            { title: translate('Common.Year'), field: 'Ano' },
            { title: translate('Common.Period'), field: 'Periodo' },
            { title: translate('Common.Average'), field: 'Media', format: '{0:n2}' },
            { field: 'Total', format: '{0:n0}' }
        ];

        const dataChart = _.map(resp, (v) => {
            return {
                [translate('Common.Month')]: v.month,
                [translate('Common.Sales')]: v.value,
                [translate('Common.Year')]: v.year
            };
        });

        const calculateSalesVariation = _calculateSalesVariation(resp, moment().year());

        return {
            chart: new DataTable(_.sortBy(dataChart, ['Ano']), {
                row: [translate('Common.Year')],
                column: [translate('Common.Month')],
                value: [translate('Common.Sales')],
                header(value) {
                    return moment(value, 'MM').format('MMM');
                }
            }),
            grid: {
                dataSource,
                columns
            },
            gridVehicles: {
                dataSource: dataSourceVehicles,
                columns: columnsVehicle,
            },
            ...calculateSalesVariation,
        };
    });
};

const _getInfo = (feature) => {
    const intL = new Intl.DateTimeFormat(translate.language());
    const data = {};
    const {
        geometry: { coordinates },
        properties: {
            Id,
            ServiceTypeId,
            OpeningDate,
            CorporateName,
            Phone,
            Name,
            AddressComplement,
            StateAcronym,
            Neighborhood,
            City,
            Zipcode,
            NetworkName,
            BrandsName,
            Document,
            DealerTypeId,
            PinName,
            IsPinTire
        },
    } = feature;

    const tipoServico = environment.get('CONFIG.FILTERS.SERVICE_TYPES').find((serviceType) => serviceType.id === ServiceTypeId);

    data.information = {
        razaoSocial: CorporateName,
        contato: Phone,
        tipo: 'Autopeças',
        rede: NetworkName,
        bandeira: BrandsName ? BrandsName.join(', ') : undefined,
        dataAberturaReceitaFederal: OpeningDate ? intL.format(new Date(OpeningDate)) : undefined,
        tipoServico: tipoServico ? tipoServico.nome : undefined,
        document: Document
            ? Document
            : '00.000.000/0000-00',
        dealerTypeName: DealerTypeId === DealerTypes.Homologated
            ? 'Sim'
            : 'Não',
    };

    data.location = {
        latitude: coordinates[1],
        longitude: coordinates[0],
    };

    data.address = {
        endereco: AddressComplement,
        bairro: Neighborhood,
        cidade: City,
        uf: StateAcronym,
        cep: Zipcode,
    };

    let pin = PinName.toUnderscoreCase();

    if (IsPinTire) {
        pin = 'pneus';
    }

    data.logo = `${environment.get('IMAGES_HOST')}/aftersales/logo_${pin}.png`;
    data.id = Id;
    data.name = Name;
    data.style = 'style=width: 42px; height: 42px;';

    return { ...feature, ...data };
};

class StoresInfoAfterSalesInfo {
    static getInstance() {
        if (!StoresInfoAfterSalesInfo._instance) {
            StoresInfoAfterSalesInfo._instance = new StoresInfoAfterSalesInfo();
        }

        return StoresInfoAfterSalesInfo._instance;
    }

    init() {
    }

    _getFeatureLayer(resultType, feature) {
        return currentLayer(resultType).getLayer(feature.properties.Id);
    }

    _triggerAction(value, layer, feature, resultType, context) {
        api.general.log.action(environment._getContextEnum(resultType), `Info - ${value}`, JSON.stringify(feature.properties));
        switch (value) {
            case 'locate':
                return worldMap.setView(layer.getLatLng(), 16);
            case 'recalculate':
                return this._recalculateArea(feature, layer, resultType);
            // case 'reporting':
            //     return LayerInfo('CreatePoint').reporting(currentLayer(resultType), feature, context);
            // case 'edit':
            //     return LayerInfo('CreatePoint').edit(currentLayer(resultType), feature, context);
            case 'remove':
                return bootbox.dialog({
                    title: '<i class="fa fa-warning"></i> Aviso',
                    message: 'Você tem certeza de que deseja remover o ponto selecionado? Esta ação não poderá ser desfeita.',
                    backdrop: false,
                    buttons: {
                        main: {
                            label: '<i class="fa fa-times"></i> Cancelar',
                            className: 'btn-default'
                        },
                        danger: {
                            label: '<i class="fa fa-trash-o"></i> Remover',
                            className: 'btn-danger',
                            callback() {
                                return api.points.remove(feature.properties.Id);
                            }
                        }
                    }
                });
        }
    }

    _showInformation(feature, item, layer, context, resultType) {
        api.general.log.action(environment._getContextEnum(resultType), 'Info - Ficha da Loja', JSON.stringify(feature.properties));
        const queue = [];
        queue.push(_getInfo(feature, resultType));
        queue.push(_getImage(feature));
        queue.push(_getSales(feature.properties.Name, feature.properties.Id, context));

        Promise.all(queue).then(response => {
            const data = {
                ...response[0]
            };
            data.image = response[1];
            data.sales = response[2];
            data.information.dealerNumber = data.properties.OriginalId;

            if (!data.sales.variation) {
                data.sales = null;
            }

            LayerInfo('ResumeAfterSales').show(data, {
                context,
                resultType,
                title: translate('Common.StoreInfo'),
                callback: value => this._triggerAction(value, layer, feature, resultType, context),
                callbackBack: () => {
                    infoBoard.showInfo(resultType, feature.properties.Id);
                    worldMap.getCurrent().fireEvent('click');
                },
                visible: true,
                latlng: layer.getLatLng()
            });
        });
    }

    _addAreaInfluence(feature, layer, context, resultType) {
        if (isAuthorized(FeaturePermission.MAP_STORE.influenceArea)) {
            this._recalculateArea(feature, layer, resultType);
        }
    }

    _recalculateArea(feature, layer, resultType) {
        LayerInfo('Summary').recalculate({
            id: feature.properties.Id,
            latlng: layer.getLatLng(),
            value: feature.properties.RaioServiceAreaMt,
            layer: currentLayer(resultType),
            controls: LayerControls(resultType).getControls(),
            update: true,
            feature
        });
    }

    _updateVoronoi(feature, layer) {
        if (layer && worldMap.hasLayer(layer)) {
            return LayerControls('Voronoi').add([feature]);
        } else {
            return LayerControls('Voronoi').remove([feature.properties.Id]);
        }
    }

    create(features, resultType) {
        if (infoBoard.locked) {
            return;
        }

        const contexts = {
            'NetworkAfterSales': {
                context: 'network',
                contextLabel: `${translate('Common.NetworkAfterSales')}`,
            },
            'CompetitorsAfterSales': {
                context: 'competitors',
                contextLabel: translate('Common.CompetitorsAfterSales'),
            },
            'StoreInactiveAfterSales': {
                context: 'storeinactive',
                contextLabel: translate('Common.StoreInactiveAfterSales'),
            }
        };

        const { context, contextLabel } = contexts[resultType];

        const container = _getContainer({
            id: resultType.toLowerCase(),
            name: contextLabel
        });

        if (infoBoard.dataUnavailable({
            features,
            container,
            isEnabled: isLayerEnabled(resultType)
        })) {
            return;
        }

        const $template = $(templates('infoboard-content', {
            id: `${resultType.toLowerCase()}-collapse`,
            actions: true
        }));
        const content = $template.find('.panel-group');

        const items = _.reduce(_(features).orderBy(x => x.properties.NetworkStoreSize, 'asc').value(), (acc, feature) => {
            let layer = this._getFeatureLayer(resultType, feature);

            const options = {
                feature,
                name: resultType.toLowerCase(),
                originalName: resultType,
                container,
                controls: {
                    info: true,
                    createInfluenceArea: isAuthorized(FeaturePermission.MAP_STORE.influenceArea, feature) && !feature.properties.PossuiServiceArea,
                    removeInfluenceArea: isAuthorized(FeaturePermission.MAP_STORE.influenceArea, feature) && feature.properties.PossuiServiceArea,
                    updateInfluenceArea: isAuthorized(FeaturePermission.MAP_STORE.influenceArea, feature) && feature.properties.PossuiServiceArea,
                    dashboardServiceArea: true,
                    hideStore: true,
                }
            };

            const item = infoBoard.createItem(options);
            if (!item) {
                return;
            }

            if (item.btn.info) {
                item.btn.info.on('click', () => {
                    this._showInformation(feature, item, layer, context, resultType);
                    selectMarker(layer, feature, resultType, context);
                });
            }

            if (item.btn.createInfluenceArea) {
                item.btn.createInfluenceArea.on('click', () =>
                    this._addAreaInfluence(feature, layer, context, resultType)
                );
            }

            if (item.btn.updateInfluenceArea) {
                item.btn.updateInfluenceArea.on('click', () =>
                    this._addAreaInfluence(feature, layer, context, resultType)
                );
            }

            if (item.btn.removeInfluenceArea) {
                item.btn.removeInfluenceArea.on('click', () => {
                    LayerInfo('Summary').deleteServiceArea({
                        id: feature.properties.Id,
                        latlng: layer.getLatLng(),
                        value: feature.properties.RaioServiceAreaMt,
                        layer: currentLayer(resultType),
                        controls: LayerControls(resultType).getControls(),
                        feature
                    });
                });
            }

            if (item.btn.hideStore) {
                // toggle icon if the current feature was target as hidden
                if (currentLayer(resultType).isHidden(feature.properties.Id)) {
                    $(item.btn.hideStore).parent().find('i').toggleClass('fa-eye-slash');
                    $(item.btn.hideStore).parent().find('a').text(translate('Action.ShowStore'));
                    item.title.toggleClass('disabled');
                    this._updateVoronoi(feature);
                }

                if (!currentLayer(resultType).isHidden(feature.properties.Id)) {
                    $(item.btn.hide).parent().find('i').toggleClass('fa-eye');
                    $(item.btn.hide).parent().find('a').text(translate('Action.HideStore'));
                }

                item.btn.hideStore.on('click', () => {
                    LayerControls(resultType).toggleVisibility(feature.properties.Id, layer);
                    if (!layer) {
                        layer = this._getFeatureLayer(resultType, feature);
                    }

                    return this._updateVoronoi(feature, layer);
                });
            }

            if (item.btn.dashboardServiceArea) {
                item.btn.dashboardServiceArea.on('click', () => {
                    return LayerControls(resultType).createDashboardServiceArea(feature);
                });
            }

            // item.title.on('mouseover', () => LayerControls(resultType).highlight(layer));

            // item.title.on 'click', (e) ->
            //  currentLayer(resultType).panTo(layer.getLatLng())

            if (LayerInfo('Compare').getStatus() && LayerInfo('Compare').check(feature.properties.Id)) {
                acc = [item.layout, ...acc];
            } else {
                acc.push(item.layout);
            }

            return acc;
        }, []);

        // give the DOM a break
        $(content).append(items);
        $(container).detach().html($template).appendTo('#infoboard-content');

        const actions = infoBoard.createActions({
            export: {
                value:
                    (context === 'network' && isAuthorized(FeaturePermission.LAYER_AFTER_SALES_NETWORK.export))
                    || (context === 'competitors' && isAuthorized(FeaturePermission.LAYER_AFTER_SALES_COMPETITORS.export)),
                label: translate('Action.ExportStores')
            },
            compare: {
                value: false // isAuthorized("#{context}-comparison-view")
            }
        }, context);

        actions.layout.appendTo(container);

        if (actions.compare) {
            $(actions.compare).on('click', () => {
                if (LayerInfo('Compare').getStatus()) {
                    return;
                }

                infoBoard.enableCompare(container);
                if (isLayerEnabled('PointOfInterest')) {
                    infoBoard.enableCompare('#points-tab');
                }

                LayerInfo('Compare').create();
            });

            // compare points
            if (LayerInfo('Compare').getStatus()) {
                infoBoard.enableCompare(container, resultType);
            }
        }

        if (actions.export) {
            $(actions.export).on('click', e => {
                e.stopPropagation();
                return exportStore(storeExportType[resultType]);
            });
        }

        customScrollbar(content);
    }
}

export default StoresInfoAfterSalesInfo;



// WEBPACK FOOTER //
// ./src/scripts/info/AfterSales/StoresInfoAfterSales.js